import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../api/client';
import { usePersistentState } from '../../lib/usePersistentState';
import { OfflineTriageList, OfflineTriageCamera, TriageReason } from '../../types';
import {
  Stethoscope,
  RefreshCw,
  KeyRound,
  Radio,
  AlertTriangle,
  HelpCircle,
  CheckCircle2,
  Phone,
  Clock,
  Loader2,
  ChevronDown,
  ChevronRight,
  Info,
  Search,
} from 'lucide-react';

// The triage board, rebuilt on real data. Every camera carries the ONE reason it
// is offline (from GET /v2/admin/devices/offline-triage) and the evidence behind
// it. The actions are only the ones that actually exist: re-probe eye4, resolve
// empty UIDs. There is no remote reboot for an offline camera, no SIM data on
// file, and phone numbers for almost no one — so the honest next step for a dead
// camera is reactivation or a human call, and the board says so plainly.

type Tone = 'danger' | 'warning' | 'purple' | 'info' | 'success';

interface ReasonMeta {
  label: string;
  short: string;
  tone: Tone;
  outage: boolean; // counts toward "real work", vs. the quiet/false-offline set
  icon: React.ReactNode;
  guidance: string;
  action: 'resolve-vuid' | 'reprobe' | 'contact' | 'none';
}

const REASONS: Record<TriageReason, ReasonMeta> = {
  needs_reactivation: {
    label: 'Needs reactivation',
    short: 'Dead',
    tone: 'danger',
    outage: true,
    icon: <AlertTriangle size={14} />,
    guidance:
      'eye4 reports this camera inactivated — genuinely dead: powered off, unregistered, or a lapsed 4G SIM. There is no remote fix; it must be reactivated or physically power-cycled on site. Reach the owner to confirm.',
    action: 'contact',
  },
  unresolved: {
    label: 'Unresolved UID',
    short: 'No UID',
    tone: 'warning',
    outage: true,
    icon: <KeyRound size={14} />,
    guidance:
      'eye4 never resolved a UID for this camera (mostly v1-migrated cameras the resolver never ran on). Resolve UIDs to see if it is recoverable — if eye4 still returns 550 it is genuinely dead and needs reactivation.',
    action: 'resolve-vuid',
  },
  never_reported: {
    label: 'Never reported',
    short: 'Never seen',
    tone: 'purple',
    outage: true,
    icon: <HelpCircle size={14} />,
    guidance:
      'Registered but has never sent a heartbeat or event — most likely never powered on or fully onboarded. Reach the owner to check installation.',
    action: 'contact',
  },
  silent_unknown: {
    label: 'Not yet probed',
    short: 'Unprobed',
    tone: 'info',
    outage: true,
    icon: <Info size={14} />,
    guidance:
      'Offline and previously reported, but eye4 has not been probed since it went quiet. Re-probe presence to classify it as reachable-quiet or dead.',
    action: 'reprobe',
  },
  reachable_quiet: {
    label: 'Alive — just quiet',
    short: 'Quiet',
    tone: 'success',
    outage: false,
    icon: <CheckCircle2 size={14} />,
    guidance:
      'eye4 says this camera is reachable; it is simply quiet — no motion means no clip, and on the real ~10-30min cadence no recent heartbeat either, so it drops out of the liveness window while being perfectly fine. This is NOT a real outage.',
    action: 'none',
  },
};

const toneVars = (tone: Tone) => ({
  bg: `var(--${tone}-bg)`,
  border: `var(--${tone}-border)`,
  text: `var(--${tone}-text)`,
  solid: `var(--${tone})`,
});

function relTime(iso?: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '—';
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// The real-outage buckets, worst first. reachable_quiet is handled separately.
const OUTAGE_ORDER: TriageReason[] = [
  'needs_reactivation',
  'unresolved',
  'never_reported',
  'silent_unknown',
];

export const OfflineTriagePage: React.FC = () => {
  const [data, setData] = useState<OfflineTriageList | null>(null);
  const [active, setActive] = useState<TriageReason>('needs_reactivation');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [quietOpen, setQuietOpen] = useState(false);
  const [quiet, setQuiet] = useState<OfflineTriageCamera[] | null>(null);

  const [job, setJob] = useState<string | null>(null); // running-job label
  const [feedback, setFeedback] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // View mode + search filter.
  const [view, setView] = useState<'reason' | 'site'>('reason');
  const [search, setSearch] = usePersistentState('offlineTriage.search', '');
  const [siteCams, setSiteCams] = useState<OfflineTriageCamera[] | null>(null);
  const [siteLoading, setSiteLoading] = useState(false);
  const [openSites, setOpenSites] = useState<Record<string, boolean>>({});

  // Whole offline set (all reasons), paged — needed to group by site.
  const loadSite = useCallback(async () => {
    setSiteLoading(true);
    try {
      const all: OfflineTriageCamera[] = [];
      for (let page = 1; page <= 20; page++) {
        const d = await api.getOfflineTriage(undefined, page, 100);
        all.push(...d.cameras);
        if (all.length >= d.pagination.totalItems || d.cameras.length === 0) break;
      }
      setSiteCams(all);
    } catch {
      setSiteCams([]);
    } finally {
      setSiteLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'site' && siteCams === null) loadSite();
  }, [view, siteCams, loadSite]);

  const matches = useCallback(
    (c: OfflineTriageCamera) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return `${c.name} ${c.serialNumber} ${c.ownerName ?? ''} ${c.ownerEmail}`.toLowerCase().includes(q);
    },
    [search],
  );

  const load = useCallback(async (reason: TriageReason) => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.getOfflineTriage(reason, 1, 100);
      setData(d);
    } catch (e: any) {
      setError(e?.message || 'Failed to load triage board');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(active);
  }, [active, load]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const counts = data?.counts;
  const outageTotal = counts
    ? counts.needsReactivation + counts.unresolved + counts.neverReported + counts.silentUnknown
    : 0;

  // Poll an async job (re-probe / backfill) to completion, then reload.
  const pollJob = (which: 'presence' | 'backfill', label: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = which === 'presence' ? await api.getPresenceStatus() : await api.getBackfillStatus();
        if (s && s.running === false) {
          if (pollRef.current) clearInterval(pollRef.current);
          setJob(null);
          const summary =
            which === 'presence'
              ? `Re-probe done: ${s.active ?? 0} reachable, ${s.inactivated ?? 0} dead, ${s.unknown ?? 0} unknown.`
              : `Resolve done: ${s.resolved ?? 0} resolved, ${s.updated ?? 0} written, ${s.inactivated ?? 0} still dead.`;
          setFeedback(summary);
          load(active);
        } else if (s && s.processed != null && s.scanned != null) {
          setJob(`${label} — ${s.processed}/${s.scanned}…`);
        }
      } catch {
        /* keep polling; transient */
      }
    }, 4000);
  };

  const runRefresh = async () => {
    setFeedback(null);
    try {
      const r = await api.refreshPresence();
      if (!r.started) { setFeedback('A presence refresh is already running.'); return; }
      setJob(`Re-probing eye4 for ${r.toProcess} silent cameras…`);
      pollJob('presence', 'Re-probing');
    } catch (e: any) {
      setFeedback(e?.message || 'Failed to start presence refresh');
    }
  };

  const runBackfill = async () => {
    setFeedback(null);
    try {
      const r = await api.backfillVuids(false);
      if (!r.started) { setFeedback('A UID backfill is already running.'); return; }
      setJob(`Resolving UIDs for ${r.toProcess} cameras…`);
      pollJob('backfill', 'Resolving UIDs');
    } catch (e: any) {
      setFeedback(e?.message || 'Failed to start UID backfill');
    }
  };

  const toggleQuiet = async () => {
    const next = !quietOpen;
    setQuietOpen(next);
    if (next && quiet === null) {
      try {
        const d = await api.getOfflineTriage('reachable_quiet', 1, 100);
        setQuiet(d.cameras);
      } catch {
        setQuiet([]);
      }
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <Stethoscope size={20} color="var(--primary-light)" />
          <h2 style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-main)' }}>Offline Triage</h2>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 720 }}>
          Every offline camera with the one real reason it is offline and the evidence behind it —
          so a bare “offline” becomes a diagnosis you can act on. Diagnoses come from eye4 presence;
          the only remote fixes that exist are re-probe and UID-resolve.
        </p>
      </div>

      {/* Global actions */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <button className="btn btn-secondary btn-sm" onClick={runRefresh} disabled={!!job}>
          <Radio size={14} /> <span>Re-probe presence</span>
        </button>
        <button className="btn btn-secondary btn-sm" onClick={runBackfill} disabled={!!job}>
          <KeyRound size={14} /> <span>Resolve UIDs{counts ? ` (${counts.unresolved})` : ''}</span>
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => load(active)} disabled={loading || !!job}>
          <RefreshCw size={14} /> <span>Refresh</span>
        </button>
        {job && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--info-text)' }}>
            <Loader2 size={14} className="spin" /> {job}
          </span>
        )}
      </div>

      {feedback && (
        <div style={{ background: 'var(--info-bg)', border: '1px solid var(--info-border)', color: 'var(--info-text)', padding: '10px 14px', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
          <Info size={15} /> <span>{feedback}</span>
        </div>
      )}

      {/* View toggle + search filter */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className={`btn btn-sm ${view === 'reason' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('reason')}>By reason</button>
          <button className={`btn btn-sm ${view === 'site' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView('site')}>By site</button>
        </div>
        <div className="search-input-wrapper" style={{ maxWidth: 340 }}>
          <Search size={15} />
          <input type="text" className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by camera, serial or owner…" />
        </div>
      </div>

      {view === 'site' ? (
        <SiteView cams={siteCams} loading={siteLoading} matches={matches} openSites={openSites} setOpenSites={setOpenSites} />
      ) : (
      <>
      {/* Bucket tabs (real outages) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
        {OUTAGE_ORDER.map((r) => {
          const m = REASONS[r];
          const t = toneVars(m.tone);
          const n = counts
            ? r === 'needs_reactivation' ? counts.needsReactivation
              : r === 'unresolved' ? counts.unresolved
                : r === 'never_reported' ? counts.neverReported
                  : counts.silentUnknown
            : 0;
          const on = active === r;
          return (
            <button
              key={r}
              onClick={() => setActive(r)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                padding: '8px 14px', borderRadius: 'var(--radius-md)', fontSize: 13, fontWeight: 500,
                background: on ? t.bg : 'var(--bg-surface-card)',
                border: `1px solid ${on ? t.border : 'var(--border-dim)'}`,
                color: on ? t.text : 'var(--text-muted)',
              }}
            >
              <span style={{ color: t.solid, display: 'inline-flex' }}>{m.icon}</span>
              {m.label}
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: on ? t.text : 'var(--text-main)' }}>{n}</span>
            </button>
          );
        })}
        <div style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 12.5, color: 'var(--text-dim)' }}>
          {counts ? `${outageTotal} need attention · ${counts.total} offline total` : ''}
        </div>
      </div>

      {/* Active bucket guidance */}
      <BucketGuidance reason={active} />

      {/* Camera list */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          <Loader2 size={22} className="spin" />
        </div>
      ) : error ? (
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', color: 'var(--danger-text)', padding: '14px 18px', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
          {error}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(data?.cameras ?? []).filter(matches).map((c) => <TriageRow key={c.id} cam={c} />)}
          {(data?.cameras ?? []).filter(matches).length === 0 && (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No cameras {search ? 'match your filter' : 'in this bucket'}.
            </div>
          )}
          {data && data.pagination.totalItems > data.cameras.length && (
            <div style={{ textAlign: 'center', fontSize: 12.5, color: 'var(--text-dim)', paddingTop: 6 }}>
              Showing {data.cameras.length} of {data.pagination.totalItems}.
            </div>
          )}
        </div>
      )}

      {/* Collapsed: alive-but-quiet (not real outages) */}
      <div style={{ marginTop: 26 }}>
        <button
          onClick={toggleQuiet}
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, padding: 0 }}
        >
          {quietOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <CheckCircle2 size={15} color="var(--success)" />
          <span style={{ fontWeight: 500 }}>
            Alive, no recent motion — {counts?.reachableQuiet ?? 0} cameras (not outages)
          </span>
        </button>
        {quietOpen && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <BucketGuidance reason="reachable_quiet" />
            {(quiet ?? []).map((c) => <TriageRow key={c.id} cam={c} />)}
            {quiet !== null && quiet.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>None.</div>
            )}
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

// Group offline cameras by owner (site), worst first. Each site collapses; the
// header shows the down count + a chip per reason so an operator sees at a glance
// which sites have the most trouble.
const SiteView: React.FC<{
  cams: OfflineTriageCamera[] | null;
  loading: boolean;
  matches: (c: OfflineTriageCamera) => boolean;
  openSites: Record<string, boolean>;
  setOpenSites: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}> = ({ cams, loading, matches, openSites, setOpenSites }) => {
  if (loading || cams === null) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={22} className="spin" /></div>;
  }
  const filtered = cams.filter(matches);
  // Group by owner email.
  const byOwner = new Map<string, { name: string; email: string; cams: OfflineTriageCamera[] }>();
  for (const c of filtered) {
    const key = c.ownerEmail || c.ownerName || '—';
    if (!byOwner.has(key)) byOwner.set(key, { name: c.ownerName || c.ownerEmail || '(unknown)', email: c.ownerEmail || '', cams: [] });
    byOwner.get(key)!.cams.push(c);
  }
  // Worst first: most attention-needing cameras (exclude reachable_quiet from the count).
  const attention = (list: OfflineTriageCamera[]) => list.filter((c) => c.reason !== 'reachable_quiet').length;
  const sites = Array.from(byOwner.values()).sort((a, b) => attention(b.cams) - attention(a.cams) || b.cams.length - a.cams.length);

  if (sites.length === 0) {
    return <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No cameras match your filter.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {sites.map((site) => {
        const key = site.email || site.name;
        const open = !!openSites[key];
        // Reason breakdown chips.
        const byReason = new Map<TriageReason, number>();
        site.cams.forEach((c) => byReason.set(c.reason, (byReason.get(c.reason) ?? 0) + 1));
        return (
          <div key={key} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              onClick={() => setOpenSites((p) => ({ ...p, [key]: !p[key] }))}
              style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 12, flexWrap: 'wrap' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                {open ? <ChevronDown size={16} color="var(--text-dim)" /> : <ChevronRight size={16} color="var(--text-dim)" />}
                <div style={{ minWidth: 0 }}>
                  <strong style={{ fontSize: 14, color: 'var(--text-main)' }}>{site.name}</strong>
                  <div style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{site.email}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {Array.from(byReason.entries()).map(([r, n]) => {
                  const m = REASONS[r]; const t = toneVars(m.tone);
                  return (
                    <span key={r} style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.text, borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
                      {m.short} {n}
                    </span>
                  );
                })}
                <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>{site.cams.length} down</span>
              </div>
            </div>
            {open && (
              <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--bg-surface)' }}>
                {site.cams.map((c) => <TriageRow key={c.id} cam={c} />)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const BucketGuidance: React.FC<{ reason: TriageReason }> = ({ reason }) => {
  const m = REASONS[reason];
  const t = toneVars(m.tone);
  return (
    <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: 16, fontSize: 13, color: t.text, display: 'flex', gap: 10 }}>
      <span style={{ color: t.solid, marginTop: 1 }}>{m.icon}</span>
      <span>{m.guidance}</span>
    </div>
  );
};

const TriageRow: React.FC<{ cam: OfflineTriageCamera }> = ({ cam }) => {
  const m = REASONS[cam.reason];
  const t = toneVars(m.tone);
  return (
    <div className="card" style={{ borderLeft: `3px solid ${t.solid}`, padding: '14px 16px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 220 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 14.5, color: 'var(--text-main)' }}>{cam.name || '(unnamed)'}</strong>
            <code style={{ fontSize: 11.5, color: 'var(--primary-light)', fontFamily: 'var(--font-mono)' }}>{cam.serialNumber}</code>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: t.bg, border: `1px solid ${t.border}`, color: t.text, borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
              {m.short}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {cam.ownerName || cam.ownerEmail}
            {cam.ownerName && <span style={{ color: 'var(--text-dim)' }}> · {cam.ownerEmail}</span>}
          </div>
        </div>

        {/* Owner contact. The account phone is on file for almost no one, so we
            fall back to a number lifted from the camera name (the real source for
            most owners) — labelled so the operator knows where it came from. */}
        <div style={{ textAlign: 'right', minWidth: 150 }}>
          {cam.ownerPhone ? (
            <a href={`tel:${cam.ownerPhone}`} className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>
              <Phone size={13} /> <span>{cam.ownerPhone}</span>
            </a>
          ) : cam.namePhone ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
              <a href={`tel:${cam.namePhone}`} className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>
                <Phone size={13} /> <span>{cam.namePhone}</span>
              </a>
              <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>from camera name</span>
            </div>
          ) : (
            <span style={{ fontSize: 11.5, color: 'var(--text-dim)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Phone size={12} /> No number on file
            </span>
          )}
        </div>
      </div>

      {/* Evidence strip */}
      <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
        <Evidence label="eye4 state" value={cam.eye4State ? cam.eye4State : 'not probed'} tone={cam.eye4State === 'active' ? 'success' : cam.eye4State === 'inactivated' ? 'danger' : undefined} />
        <Evidence label="eye4 checked" value={relTime(cam.eye4CheckedAt)} icon={<Clock size={11} />} />
        <Evidence label="last signal" value={relTime(cam.lastSignalAt)} icon={<Clock size={11} />} />
        <Evidence label="UID" value={cam.vuid ? 'resolved' : 'empty'} tone={cam.vuid ? undefined : 'warning'} />
        {cam.vuidCluster && <Evidence label="cluster" value={cam.vuidCluster} />}
      </div>
    </div>
  );
};

const Evidence: React.FC<{ label: string; value: string; icon?: React.ReactNode; tone?: Tone }> = ({ label, value, icon, tone }) => (
  <div>
    <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>{label}</div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: tone ? `var(--${tone}-text)` : 'var(--text-main)', fontWeight: 500 }}>
      {icon}{value}
    </div>
  </div>
);
